import '@datum-sdk/engine/index.css';
import './sdkPoiMarkers.css';
import poiMoveToIconSvg from '../../icons/poi-move-to.svg?raw';
import poiBeaconIconUrl from '../../icons/poi-beacon.svg?url';
import { DatumEngine } from '@datum-sdk/engine';
import type { DeepReadonly, EditorHostEventMap, Poi, Scene } from '@datum-sdk/engine';
import type { ColliderConfig } from '@datum-sdk/scene-format';
import type { Sight } from '../../types';
import type { QualityProfile } from '../qualityProfile';
import type { OverviewCameraPose, SplatCameraPose, SplatSceneApi } from '../splatSceneApi';
import type { SceneConfig } from '../sceneConfig';
import {
  sceneLoadingFailed,
  sceneLoadingFinished,
  sceneLoadingStarted,
  scenePreviewLoadingFinished,
  splatFetchCompleted,
  splatInitCompleted,
  splatLoadingProgressed,
} from '../../state/sceneLoading';
import { NATIVE_SDK_SCENES, SDK_PRELOADER_CINEMATIC_SCENES } from './nativeScenes';
import { isOutsideContainment } from './containment';
import { createSkyboxFade } from './skyboxFade';
import { swapUnreachableCdnAssets } from './swapUnreachableCdnAssets';
import { watchSceneGrab } from './watchSceneGrab';
import { readSceneMarkers } from './readSceneMarkers';
import { mountTierOverlay } from './tierOverlay';
import { mountSdkCrow, type SdkCrow } from './crow/mountSdkCrow';
import {
  EASE_IN_OUT_CUBIC,
  createNativeRouteCinematic,
  preloaderRestPose,
} from './nativeRouteCinematic';
import { createDevEditorTools } from './devEditorTools';
import { $appConfig, $devConfig } from '../../state/appConfig';
import { splatDevHandlesReady } from '../../state/splatDevTools';
import { goToSight, userSelectedSight } from '../../state/appState';
import { assistantPoiClickRequested } from '../../state/assistantHud';
import { enterInsideScene } from '../../state/insideSceneEntry';
import { sdkCameraPoseGetterReady, sdkSceneMarkersGetterReady } from '../../state/sceneState';
import { isInsideScene } from '../insideScenes/registry';
import { insidePoiClicked } from '../../analytics/navigationTracker';
import {
  cameraPreloaderSkipped,
  cameraSessionSourceReady,
} from '../../analytics/cameraSessionTracker';
import { trackWebGlContextLost } from '../../analytics/performanceTracker';
import { sceneEntered, scenePreviewMissed } from '../../analytics/sceneTracker';
import {
  decrementScene,
  decrementWebGLContext,
  incrementScene,
  incrementWebGLContext,
  trackPostDispose,
} from '../../debug/memoryTracker';
import { $locale } from '../../state/locale';
import { LOCALE_TEXT_DIRECTION } from '../../shared/locale';
import { getMessages } from '../../i18n';
import { KNOWN_SCENES, SCENE_POI_ID } from '../sceneNavigationMap';
import { enterScene, resetNavigation } from '../../state/sceneNavigation';
import { createViewportPaddingTween } from './viewportPaddingTween';
import { HUD_REVEAL_DELAY } from '../../context/CameraViewOffsetContext';

const NOOP = (): void => {};

/**
 * Chat-panel camera view-offset, written by `CameraViewOffsetContext`
 * (`applyCurtainOffset` → `updateCameraConfig`) — scene-agnostic. Fed to the
 * engine's viewport padding so the scene reframes as the chat panel resizes.
 * Module-level derived store; per-scene `.watch`.
 */
const $viewOffset = $appConfig.map((c) => c.camera.viewOffset);

/**
 * Orbit camera pose for a sight — mirrors `SightManager.getCameraSetupForSight`:
 * position = sight + (d·sinθ·cosφ, d·cosθ + height, d·sinθ·sinφ), look at the
 * sight. Returns null when the sight has no orbit cameraConfig.
 */
export const orbitPoseForSight = (
  sight: Sight,
): {
  position: [number, number, number];
  lookAt: [number, number, number];
  fov?: number;
} | null => {
  const cc = sight.cameraConfig;
  if (!cc || cc.distance == null || cc.azimuth == null || cc.polar == null) return null;
  const { distance, height = 0, azimuth, polar, fov } = cc;
  return {
    position: [
      sight.position.x + distance * Math.sin(polar) * Math.cos(azimuth),
      sight.position.y + distance * Math.cos(polar) + height,
      sight.position.z + distance * Math.sin(polar) * Math.sin(azimuth),
    ],
    lookAt: [sight.position.x, sight.position.y, sight.position.z],
    fov: fov ?? undefined,
  };
};

/**
 * Travel-scaled POI flight duration (seconds). Travel-scaled (NOT the backend
 * `sight.transitionDuration` — Pilgrim moved away from it) so a short hop stays
 * proportionate and a long one doesn't teleport.
 *
 * Tuned SLOWER than Pilgrim's orbit envelope (0.3/1.2/1.0) on purpose: it was
 * the only lever while `setCameraState({isAnimated})` ran LINEAR (constant
 * velocity, hard stop) — stretching the duration softened the motion without
 * ever decelerating. The SDK now takes `easing` on that transition (see
 * DATUM_SDK_CHANGE_REQUESTS.md § C2) and `flyTo` passes `POI_FLIGHT_EASING`, so
 * the arrival is genuinely eased; the stretched envelope is kept as-is (it reads
 * well) — a candidate to re-tune back toward legacy's 0.3/1.2/1.0 by eye.
 *
 * The FPS envelope keeps the legacy SightManager constants (0.3–1.4 s @ 4 m/s
 * at world scale 1: a 2 m walk-up ≈ 500 ms).
 */
/**
 * Timing curve of every POI flight — legacy parity (`SightManager` used a
 * quadratic ease-out, `t·(2−t)`: gentle arrival, no deceleration cliff). Also
 * what the SDK's cluster approach uses by default (`clustering.easing`), so a
 * marker click and a cluster click feel like the same move.
 */
const POI_FLIGHT_EASING = 'easeOut';
const POI_FLIGHT_MIN_S = 0.5;
const POI_FLIGHT_MAX_S_ORBIT = 1.5;
const POI_FLIGHT_SPEED_ORBIT = 0.6;
const POI_FLIGHT_MIN_S_FPS = 0.3;
const POI_FLIGHT_MAX_S_FPS = 1.4;
const POI_FLIGHT_SPEED_FPS = 4.0;
const flightDurationS = (travelUnits: number, fps: boolean): number =>
  fps
    ? Math.min(
        POI_FLIGHT_MAX_S_FPS,
        Math.max(POI_FLIGHT_MIN_S_FPS, travelUnits / POI_FLIGHT_SPEED_FPS),
      )
    : Math.min(
        POI_FLIGHT_MAX_S_ORBIT,
        Math.max(POI_FLIGHT_MIN_S, travelUnits / POI_FLIGHT_SPEED_ORBIT),
      );

/**
 * Map a scene's sights to datum-sdk POI annotations. The engine renders the
 * markers and flies the camera to the POI's `camera` block on marker click —
 * the native, Path-B way (clustering is the SDK's, set per scene in nativeScenes).
 *
 * FPS scenes carry NO `camera` block (it's optional): the legacy walk-up pose
 * is computed AT CLICK TIME from wherever the viewer currently stands
 * (approach direction + preserved eye level) — inexpressible as a static
 * pose. Native focus is disabled anyway; the `poi:select` handler flies.
 *
 * An orbit sight without a full `cameraConfig` keeps its marker too, with no
 * `camera` block: a pick still reaches the backend, the camera stays put.
 * Legacy drew the marker and threw on its fly-to (`getCameraSetupForSight`).
 */
const buildSdkPois = (
  sights: Sight[],
  fps: boolean,
  colliders: readonly ColliderConfig[],
): Poi[] => {
  const pois: Poi[] = [];
  for (const sight of sights) {
    const pose = fps ? null : orbitPoseForSight(sight);
    const inside = typeof sight.moveToSceneId === 'string' && isInsideScene(sight.moveToSceneId);
    const position: [number, number, number] = [
      sight.position.x,
      sight.position.y,
      sight.position.z,
    ];
    pois.push({
      id: String(sight.id),
      label: sight.name ?? String(sight.id),
      position,
      // A POI beyond the camera cage would be dimmed by the cage wall forever
      // (legacy cages never block the line of sight). Only ever `true` — an
      // explicit `false` would override a scene-level `disableOcclusion`.
      ...(isOutsideContainment(position, colliders) ? { disableOcclusion: true } : {}),
      // No animationDuration — native focus is disabled; the poi:select handler
      // flies with a travel-scaled duration (flightDurationS).
      ...(pose
        ? {
            camera:
              pose.fov != null
                ? { position: pose.position, lookAt: pose.lookAt, fov: pose.fov }
                : { position: pose.position, lookAt: pose.lookAt },
          }
        : {}),
      // Inside-scene nav POI ("Inside the Kaaba"): custom marker content with
      // the legacy icon badge (renderInsidePoiMarker dispatches on
      // `data.insideScene`). `labelPlacement: 'none'` suppresses the engine
      // label — the custom content owns the pill; the label text mirrors onto
      // the marker root's aria-label.
      ...(inside ? { labelPlacement: 'none' as const, data: { insideScene: true } } : {}),
    });
  }
  return pois;
};

/**
 * Custom marker content (`renderPoi`) for inside-scene nav POIs — legacy
 * parity: Pilgrim rendered "Inside the Kaaba"-style sights as dot + label pill
 * with a trailing icon BADGE inside the pill (`.has-icon` +
 * `.scene-poi-marker__icon` in PoiMarkerManager). The SDK's declarative
 * `Poi.icon` replaces the DOT, not the label content, so the badge-in-pill
 * look needs custom content. The elements REUSE the SDK's `__dot` / `__label`
 * classes so the base geometry and the re-skin in sdkPoiMarkers.css apply
 * unchanged; the badge itself is styled by the `.sdk-poi-marker-icon` rules
 * there. Every other POI returns null → default engine visual.
 */
const renderInsidePoiMarker = (poi: DeepReadonly<Poi>): HTMLElement | null => {
  if (!poi.data?.insideScene) return null;
  const content = document.createElement('span');
  const dot = document.createElement('span');
  dot.className = 'dsdk-poi-marker__dot';
  dot.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'dsdk-poi-marker__label has-icon';
  label.dir = document.documentElement.dir;
  const icon = document.createElement('span');
  icon.className = 'sdk-poi-marker-icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML = poiMoveToIconSvg; // static bundled asset, not scene data
  label.append(document.createTextNode(poi.label ?? poi.id), icon);
  content.append(dot, label);
  return content;
};

/**
 * Custom marker content for navigation-POI "beacons" (`overall` root map):
 * label on top → thin light beam → glowing diamond whose CENTER sits on the
 * 3D anchor. The SDK marker root always centers on the projection and has no
 * anchor/offset fields, so the geometry lives entirely in CSS: the root is
 * collapsed to a 0×0 point (`--dsdk-poi-size: 0px` via `[data-poi-id^='nav:']`
 * in sdkPoiMarkers.css) and this content bottom-anchors itself with a constant
 * screen-space hover lift — a static-CSS equivalent of the legacy per-frame
 * lift (the lift is constant in screen space).
 * The `<img>` keeps the legacy isolation trick: each beacon's SVG is its own
 * document, so internal filter/gradient IDs can't clash across markers.
 */
const renderNavBeaconMarker = (poi: DeepReadonly<Poi>): HTMLElement | null => {
  if (!poi.data?.navBeacon) return null;
  const content = document.createElement('span');
  content.className = 'sdk-poi-beacon';
  const label = document.createElement('span');
  label.className = 'sdk-poi-beacon__label';
  label.dir = document.documentElement.dir;
  label.textContent = poi.label ?? poi.id;
  const glyph = document.createElement('span');
  glyph.className = 'sdk-poi-beacon__glyph';
  glyph.setAttribute('aria-hidden', 'true');
  const img = document.createElement('img');
  img.src = poiBeaconIconUrl;
  img.alt = '';
  img.draggable = false;
  glyph.append(img);
  content.append(label, glyph);
  return content;
};

const renderPilgrimPoiMarker = (poi: DeepReadonly<Poi>): HTMLElement | null =>
  renderNavBeaconMarker(poi) ?? renderInsidePoiMarker(poi);

/**
 * Synthetic navigation POIs (`overall` root map) — one beacon per
 * `config.navigationPois` entry. Not backend sights: ids are `nav:<target>`,
 * labels come from i18n `globalMap.markerLabels` (locale snapshotted at scene
 * mount — legacy parity, locale switch remounts the scene), NO `camera` block
 * (click rides `poi:click` → the nav cinematic, never a native fly).
 */
const buildNavBeaconPois = (config: SceneConfig): Poi[] => {
  const navigationPois = config.navigationPois ?? [];
  if (navigationPois.length === 0) return [];
  const markerLabels = getMessages($locale.getState()).globalMap.markerLabels;
  return navigationPois.map((poi) => ({
    id: `nav:${poi.targetSceneId}`,
    label: markerLabels[poi.labelKey] ?? poi.targetSceneId,
    position: [poi.position.x, poi.position.y, poi.position.z],
    labelPlacement: 'none' as const,
    data: { navBeacon: true },
  }));
};

const EMPTY_POSE: SplatCameraPose = {
  position: { x: 0, y: 0, z: 0 },
  target: { x: 0, y: 0, z: 0 },
  fov: 60,
};
// Module-level like NOOP: a getter declared in the scene closure would pin it.
const EMPTY_POSE_GETTER = (): SplatCameraPose => EMPTY_POSE;

export interface SdkSceneOptions {
  container: HTMLElement;
  sights: Sight[];
  config: SceneConfig;
  qualityProfile?: QualityProfile;
  skipAnimations?: boolean;
  initialCameraPose?: SplatCameraPose;
}

/**
 * Renders a Pilgrim splat scene via `@datum-sdk/engine` behind `SplatSceneApi`
 * (`splatSceneApi.ts`), the interface `SceneCanvas` and the app layer use.
 *
 * Covers: engine lifecycle, the native scene (nativeScenes.ts), the loading-FSM
 * bridge (preloader / reveal / sticky-error), the preloader cinematic, POI
 * markers + flights, and the chat-panel reframe (engine viewport padding).
 */
export const createSdkScene = (opts: SdkSceneOptions): SplatSceneApi => {
  const { container, config, qualityProfile } = opts;
  const isMobile = qualityProfile?.deviceClass === 'mobile';
  const quality = qualityProfile?.tier ?? 'high';
  // Touch CAPABILITY (host policy, not device class): the SDK maps gestures but
  // deliberately never touch-detects (C3) — the host decides. On touch we disable
  // orbit pan so the default two-finger `dolly-pan` drops its pan sub-step → two
  // fingers zoom-only, one finger still rotates (desktop keeps right-drag pan).
  // 1:1 with legacy Pilgrim's `controls.enablePan = !isTouchDevice`.
  const isTouch = 'ontouchstart' in globalThis || navigator.maxTouchPoints > 0;
  // Dev mode gate: a dev build OR an explicit ?dev param. Gates the
  // SplatDevPanel handles publication and the dev camera toggles below.
  // `?dev` alone is what mounts the panel (App.tsx `showDebugControls`).
  const hasDevParam = new URLSearchParams(window.location.search).has('dev');
  const isDevMode = import.meta.env.DEV || hasDevParam;
  // `?flight` mounts the route editor (FlightEditorPanel) in any build: it
  // needs the editor tools, not dev mode.
  const hasFlightParam = new URLSearchParams(window.location.search).has('flight');
  const devConfig = $appConfig.getState().dev;

  // Every SDK-routed scene is authored natively in SDK form (nativeScenes.ts).
  // `SceneCanvas` only routes allowlisted ids here, and each MUST have a native
  // builder — the legacy `SceneConfig → Scene` translator has been removed (it
  // was the source of the orientation / size / "soapy" / reveal bugs).
  const nativeBuilder = NATIVE_SDK_SCENES[config.id];
  if (!nativeBuilder) {
    throw new Error(`[createSdkScene] no native SDK scene authored for "${config.id}"`);
  }
  const scene: Scene = nativeBuilder(config, { isMobile, quality, isTouch });
  // The half-screen chat leaves a 252 px band on a phone — under the engine's
  // default floor (min(320, 0.75·H)), which would shrink our viewport padding to
  // keep a taller band and frame the subject below the band's centre. The chat
  // panel owns the layout, so the floor goes.
  scene.settings = {
    ...scene.settings,
    layout: { ...scene.settings?.layout, minViewportHeight: 0 },
  };
  // Dev toggle `disableForcedViewMode` (legacy parity, "requires reload"):
  // forced-FPS scenes fall back to orbit for tuning.
  const controlsMode =
    config.controls?.forcedViewMode === 'first-person' &&
    !(isDevMode && devConfig.disableForcedViewMode)
      ? 'fps'
      : 'orbit';
  const isFps = controlsMode === 'fps';
  // SDK-native POI markers: the engine renders them + flies on marker click.
  // Built from the scene's sights here (nativeScenes is a pure config→Scene
  // mapper and doesn't have the resolved sights). FPS scenes get pois WITHOUT
  // camera blocks — walk-up poses are computed at click time (see fpsPoseForSight).
  const sdkPois = buildSdkPois(opts.sights, isFps, scene.settings?.collisions?.colliders ?? []);
  // Synthetic nav-POI beacons (overall root map): built from
  // config.navigationPois, rendered via renderNavBeaconMarker, clicks ride
  // poi:click → navigateToScene (see the poi:click bind).
  const navBeaconPois = buildNavBeaconPois(config);
  if (sdkPois.length > 0 || navBeaconPois.length > 0) {
    // A marker CLICK selects the POI (the SDK default) — backend sights need
    // it: their fly rides `poi:select`. Nav beacons must not be selected: their
    // click rides `poi:click` alone, so no `.dsdk-is-selected` styling ever
    // sticks to a beacon. The flag is scene-wide, so it is set only where
    // sdkPois is empty (`overall`).
    // `disableFocusOnSelect: true` — we fly on the resulting `poi:select` event
    // ourselves (see below), so a marker click AND a chat chip (→
    // `engine.selectPoi`) share ONE fly path (no double-fly).
    scene.annotations = {
      ...(scene.annotations ?? {}),
      pois: [...sdkPois, ...navBeaconPois],
      disableClickSelection: sdkPois.length === 0,
      disableFocusOnSelect: true,
    };
  }
  // Render quality. Spark's default `blurAmount` is 0.3 (soft splats); Pilgrim
  // renders sharp (`blurAmount: 0`, `preBlurAmount: 0.1`). Carry the quality
  // fields from the detected profile so splats aren't "soapy", and force a
  // 2-tier `deviceTier` so the SDK doesn't mis-detect low → cap DPR /
  // downscale-on-motion → "jagged pixels".
  const rs = qualityProfile?.renderSettings;
  const deviceTier = quality === 'low' ? 'low' : 'high';
  const renderSettings = rs
    ? {
        blurAmount: rs.blurAmount,
        preBlurAmount: rs.preBlurAmount,
        maxStdDev: rs.maxStdDev,
        minPixelRadius: rs.minPixelRadius,
        maxPixelRadius: rs.maxPixelRadius,
        minAlpha: rs.minAlpha,
        clipXY: rs.clipXY,
        falloff: rs.falloff,
        focalAdjustment: rs.focalAdjustment,
        // Perf budgeting is already expressed by our tiers (splat density +
        // pixelRatio + radius); the SDK's tier presets would double-cut on top:
        // `motionPixelRatioScale` 0.75/0.66 drops the DPR while the camera
        // moves (the "tiers look much worse on mobile" report — quality is
        // judged mid-drag), the low-tier `maxPixelRatio: 1.5` cap could lower
        // an idle DPR below our profile value, and the low-tier
        // `minSortIntervalMs: 33` throttles splat sorting to ~30 Hz → popping
        // while rotating. Motion and sort are softened from the legacy constant
        // DPR and per-frame sort, which froze more than legacy on devices:
        // 0.9 is a near-invisible mid-gesture downscale, and
        // 16 ms caps sorting at ~60 Hz — a no-op at 60 Hz, half the sorts at 120.
        // Our `pixelRatio` is a CAP; the SDK's same-named field is a base DPR
        // (a 1× screen would render at 2×), so it goes in as the cap only:
        // min(devicePixelRatio, profile) — legacy `setRendererSize`.
        maxPixelRatio: rs.pixelRatio,
        motionPixelRatioScale: 0.9,
        minSortIntervalMs: 16,
        // Not passed: `originDistance`, the one dropped field that varies by tier
        // (mobile 0.4 / 0.5 / 0.6, desktop 1.0) — Spark 2 has no such field, nor
        // `sortDistance` / `sortCoorient` / `depthBias` / `sort360`. The other
        // legacy fields are tier-invariant, left to the SDK.
      }
    : undefined;

  // The app's language, not the browser's: the engine words its cluster labels
  // in it and writes `dir` on its own container. A locale switch remounts the
  // scene, so the mount-time value is the live one (as for the beacon labels).
  const locale = $locale.getState();
  const engine = new DatumEngine({
    container,
    controlsMode,
    language: locale,
    direction: LOCALE_TEXT_DIRECTION[locale],
    // Warnings and errors: a preview or a sky that fails to load, or a settings
    // block the load drops, is only the engine's own `warn` / `error` — no event.
    logLevel: 'warn',
    deviceTier,
    renderSettings,
    cameraController: {
      // Hide the SDK's built-in controls HUD (FPS/Orbit/WASD widget) — Pilgrim
      // has its own UI; the SDK overlay isn't part of it.
      controlsHUD: { disabled: true },
      // The SDK's per-mode speed tier defaults to fps ×3 / orbit ×5 (keyboard
      // orbit and FPS movement only); ×1 keeps the pace these scenes were tuned at.
      base: { modeSpeedMultipliers: { fps: 1, orbit: 1 } },
    },
    // Inside-scene nav POIs get custom marker content (icon badge in the label
    // pill — legacy parity); every other POI keeps the default visual.
    renderPoi: renderPilgrimPoiMarker,
  });
  const engineCanvas = container.querySelector('canvas');
  incrementWebGLContext('splat');
  incrementScene('splat');
  // Unsubscribed before `engine.destroy()`, which loses the context on purpose.
  const stopContextLossTracking = engineCanvas ? trackWebGlContextLost(engineCanvas) : NOOP;

  let disposed = false;
  // `Camera Session` source: the engine's canvas and camera.
  if (engineCanvas) {
    cameraSessionSourceReady({
      canvas: engineCanvas,
      readCamera: () => {
        if (disposed) return null;
        const {
          position: [px, py, pz],
          lookAt: [tx, ty, tz],
        } = engine.getCameraState();
        return { position: { x: px, y: py, z: pz }, target: { x: tx, y: ty, z: tz } };
      },
    });
  }
  // Rotation constraint — legacy vertical camera limits, armed via the runtime
  // constraints API (no scene-format fields exist — CR C7 orbit / CR C8 fps).
  // Mode-dependent derivation (pitch > 0 = looking up, `polar = pitch + π/2`):
  //   - ORBIT scenes: `orbitMinPolarAngle` (e.g. arafat's 70°: no overhead
  //     views) → `pitch.min` only; the SDK's orbit-limits derivation maps it
  //     onto the native spherical clamp (smooth, soft-zone braking).
  //   - FPS scenes: legacy FirstPersonControls' `verticalMin/Max` look cone
  //     (mina: 30°–150° polar → pitch ∈ [−60°, +60°]) → per-frame euler clamp
  //     with a soft brake on the damped mouse path (hard stop on touch — same
  //     as legacy's hard `_lat` clamp).
  // THREE arm sites, engine behaviours behind each (all verified):
  //   1. `loadScene` WIPES constraints ("a fresh scene starts unconstrained")
  //      — so for scenes WITHOUT a preloader cinematic the arm lives in
  //      `loadScene().then()`, after the restore pose;
  //   2. cinematic scenes arm at `onCinematicDone` instead — `setCameraState`
  //      applies active constraints INSIDE the call (camera-constraints.ts
  //      "Both call sites — the per-frame pass and setCameraState"), so an
  //      early arm would clamp the flight's per-frame writes. Muzdalifah is
  //      the concrete case: its turntable rides polar 70.3° against the 70°
  //      min, and the tour-exit chord dips to ~polar 28° (pitch −62° vs the
  //      −20° limit) — legacy flies the same path under suspendOrbitLocks().
  //   3. `flyTo` clears constraints for each POI flight (the per-frame clamp
  //      would fight the moving camera) — re-armed in the poiFlyTimer
  //      callback when the flight lands.
  // Scenes without vertical limits (kaaba: minPolar 0) arm nothing.
  const pitchConstraint = (() => {
    if (isFps) {
      if (!config.controls?.constrainVertical) return null;
      const vMin = config.controls.verticalMin ?? Math.PI / 3;
      const vMax = config.controls.verticalMax ?? (2 * Math.PI) / 3;
      return { rotation: { pitch: { min: Math.PI / 2 - vMax, max: Math.PI / 2 - vMin } } };
    }
    const minPolarRad = config.controls?.orbitMinPolarAngle ?? 0;
    return minPolarRad > 0 ? { rotation: { pitch: { min: minPolarRad - Math.PI / 2 } } } : null;
  })();
  // Dev toggle `unlockCameraConstraints` (LIVE — legacy suspendOrbitLocks
  // parity): drops the pitch constraint AND opens the orbit limits wide;
  // toggling back restores the resolved pre-unlock orbit settings and re-arms.
  // All arm sites route through armCameraConstraints, which no-ops while
  // unlocked. Y stays bounded by the containment collider (legacy caveat:
  // "Y-clamp not affected"); collider-derived limits still tighten per frame.
  let constraintsUnlocked = false;
  const armCameraConstraints = (): void => {
    if (!disposed && !constraintsUnlocked && pitchConstraint) {
      engine.setCameraConstraints(pitchConstraint);
    }
  };
  let savedOrbitSettings: {
    minDistance?: number;
    maxDistance?: number;
    maxPolarAngle?: number;
  } | null = null;
  const applyConstraintsUnlock = (unlock: boolean): void => {
    if (disposed || unlock === constraintsUnlocked) return;
    constraintsUnlocked = unlock;
    if (unlock) {
      const resolved = engine.getCurrentCameraControllerSettings().orbit;
      savedOrbitSettings = {
        minDistance: resolved?.minDistance,
        maxDistance: resolved?.maxDistance,
        maxPolarAngle: resolved?.maxPolarAngle,
      };
      engine.setCameraConstraints(null);
      const current = engine.getSceneCameraControllerSettings() ?? {};
      engine.setSceneCameraControllerSettings({
        ...current,
        orbit: {
          ...(current.orbit ?? {}),
          minDistance: 0,
          maxDistance: 10000,
          maxPolarAngle: Math.PI,
        },
      });
    } else {
      const current = engine.getSceneCameraControllerSettings() ?? {};
      engine.setSceneCameraControllerSettings({
        ...current,
        orbit: { ...(current.orbit ?? {}), ...(savedOrbitSettings ?? {}) },
      });
      savedOrbitSettings = null;
      armCameraConstraints();
    }
  };
  // The preloader's routes apply no orbit limits, but collisions they meet: every
  // natural route end outside the cage (2000 B.C.E's tour starts 1 cm above its
  // roof) eases the camera back in for 300 ms. They come off for the cinematic
  // and back at `onCinematicDone` — legacy runs its preloader the same way — and
  // flip only while no route plays: toggling them under a flight in an orbit
  // scene switches the camera mode. Off before the first route, on after the
  // last. The runtime switch leaves the scene document alone, and only a scene
  // with collisions on is touched.
  let cinematicCollisionsSuspended = false;
  const suspendCinematicCollisions = (): void => {
    if (scene.settings?.collisions?.enabled) {
      cinematicCollisionsSuspended = true;
      engine.setRuntimeOptions({ collisions: { enabled: false } });
    }
  };
  const restoreCinematicCollisions = (): void => {
    if (cinematicCollisionsSuspended) {
      cinematicCollisionsSuspended = false;
      engine.setRuntimeOptions({ collisions: { enabled: true } });
    }
  };
  let poiFlying = false;
  let poiFlyTimer = 0;
  const cleanups: Array<() => void> = [];
  const bind = <K extends keyof EditorHostEventMap>(
    name: K,
    cb: (event: EditorHostEventMap[K]) => void,
  ): void => {
    engine.on(name, cb);
    cleanups.push(() => engine.off(name, cb));
  };

  // --- preloader cinematic (legacy preview-tour + flyover parity) -----------
  // The scene's own camera routes, played by id (nativeRouteCinematic.ts).
  // Opt-in per scene (SDK_PRELOADER_CINEMATIC_SCENES). Skipped on
  // skipAnimations (snapshot restore / dev) — the restore pose or the routes'
  // rest pose is applied instantly in loadScene().then() instead — and on the
  // dev toggle `unlockCameraDuringPreloader` (legacy parity: no tour/flyover/
  // blocker, the reveal still plays, camera free from the first frame).
  //
  // Dev `preloaderHoldSec`: an artificial delay AFTER the splat is ready —
  // the preview turntable keeps spinning for N extra seconds before the tour
  // exit / reveal / flyover run (consumed in the scene:loaded choreography).
  const preloaderHoldMs =
    isDevMode && devConfig.preloaderHoldSec > 0 ? devConfig.preloaderHoldSec * 1000 : 0;
  const cinematic =
    SDK_PRELOADER_CINEMATIC_SCENES.has(config.id) &&
    !opts.skipAnimations &&
    !(isDevMode && devConfig.unlockCameraDuringPreloader)
      ? createNativeRouteCinematic(engine)
      : null;
  let cinematicDone = cinematic === null;
  // Re-enables SDK controls + removes the DOM blocker; assigned when the
  // cinematic exists. Called once at cinematic end (and again harmlessly from
  // the dispose cleanups).
  let releaseCinematicInput: () => void = NOOP;
  if (cinematic) {
    cleanups.push(cinematic.dispose);
    // Input barrier for the whole cinematic, both layers (legacy parity):
    //  1. SDK controls off — pointer/wheel/keyboard ignored by the engine;
    //  2. a transparent DOM blocker over the host — swallows events before
    //     they reach the canvas AND doubles as the skip-gesture surface
    //     (legacy attached skip listeners to its previewInputBlocker too).
    engine.setRuntimeOptions({ controls: { enabled: false } });
    const inputBlocker = document.createElement('div');
    inputBlocker.style.cssText =
      'position:absolute;inset:0;z-index:30;touch-action:none;background:transparent;';
    container.appendChild(inputBlocker);
    // Skip: honored only during the flyover (canSkip) — the preview tour is
    // not skippable, same as legacy. One-shot on success; the analytics event
    // matches the legacy skip site (cameraSessionTracker).
    const onSkipGesture = (): void => {
      if (!cinematic.skip()) return;
      cameraPreloaderSkipped();
      inputBlocker.removeEventListener('pointerdown', onSkipGesture);
      inputBlocker.removeEventListener('wheel', onSkipGesture);
    };
    inputBlocker.addEventListener('pointerdown', onSkipGesture);
    inputBlocker.addEventListener('wheel', onSkipGesture);
    releaseCinematicInput = () => {
      releaseCinematicInput = NOOP;
      inputBlocker.removeEventListener('pointerdown', onSkipGesture);
      inputBlocker.removeEventListener('wheel', onSkipGesture);
      inputBlocker.remove();
      if (!disposed) engine.setRuntimeOptions({ controls: { enabled: true } });
    };
    cleanups.push(() => releaseCinematicInput());
  }
  // The SDK shows POI markers the moment appearance reaches 'scene' (≈ reveal
  // end) — mid-flight. Legacy enables them 200 ms AFTER the flyover lands.
  // Hide the layer with inline styles (they survive the SDK's own class
  // toggles on the same element) and clear them at cinematic end + delay; the
  // layer's base 0.3 s opacity transition then fades the markers in.
  const poiLayerElement = (): HTMLElement | null =>
    container.querySelector<HTMLElement>('.dsdk-poi-layer');
  const hidePoiLayer = (): void => {
    const el = poiLayerElement();
    if (el) {
      el.style.opacity = '0';
      el.style.pointerEvents = 'none';
    }
  };
  const showPoiLayer = (): void => {
    const el = poiLayerElement();
    if (el) {
      el.style.removeProperty('opacity');
      el.style.removeProperty('pointer-events');
    }
  };

  // ponytail: this note is about POSITION bounds only — the ROTATION (pitch)
  // constraint above IS in use for min-polar scenes.
  // Y limit rides the containment cylinder (scene-boundary), NOT a
  // separate position-bounds clamp. The old code set a HARD Y `bounds` via
  // setCameraConstraints (floor `clampMinHeight` / ceiling `clampMaxHeight`) —
  // that was the "Y harsher than the SDK demo" report: bounds is a post-hoc
  // Cartesian clamp, while the demo (and our X/Z) limit through the containment
  // cylinder → smooth spherical polar clamp (soft-contact mechanism 5). Dropping
  // it lets Y take the same smooth path. Trade-off: Y range = the cylinder's
  // world-Y extent (wider than the old 0.08–0.43); tighten the scene-boundary
  // cylinder's Y in nativeScenes if the framing needs it (mind preview poses).
  // Restore the bounds block from git to get the tighter static Y back.
  // Also drops the never-worked zoom-interpolated ceiling (clampMaxHeightFar);
  // if it's wanted, it belongs in the containment derivation, not a per-frame
  // setCameraConstraints re-arm (that pinned the idle render tier — see git).

  // Camera view-offset — the scene reframes smoothly as the chat panel changes
  // height, matching Pilgrim. The chat panel writes the covered canvas edges
  // into `$appConfig.camera.viewOffset` (scene-agnostic). The engine renders
  // into a FULL-VIEWPORT host (SceneCanvas `.sdkHost`) clipped by `.canvasRoot`,
  // so — like legacy — the canvas never resizes on a panel change; only the clip
  // + the engine's viewport padding move (see viewportPaddingTween.ts).
  const viewportPadding = createViewportPaddingTween(engine);
  let viewOffsetFirstApply = true;
  const unwatchViewOffset = $viewOffset.watch((viewOffset) => {
    if (disposed) return;
    // The current store value on subscribe lands instantly; later panel changes
    // animate with the chat's CSS-synced easing.
    viewportPadding.apply(viewOffset, !viewOffsetFirstApply && Boolean(viewOffset.animate));
    viewOffsetFirstApply = false;
  });
  cleanups.push(unwatchViewOffset, viewportPadding.dispose);

  // Skybox fade-in (legacy parity on public API — skyboxFade.ts). The engine
  // shows the sky only once the reveal completes (CR E1), so the fade starts
  // there rather than alongside the reveal as legacy's does. The scene loads
  // with the sky at intensity 0. Skipped on skipAnimations (legacy's instant
  // path) and for scenes without a sky (night muzdalifah). A sky that finishes
  // loading after the reveal shows at whatever the fade has reached — it is a
  // fraction of the splat's size, so in practice it is ready first. Set up only
  // once the CDN probe has settled the sky's URL: the fade writes the whole
  // skybox object back on every frame.
  const SKYBOX_FADE_DURATION_FALLBACK_MS = 1500;
  let skyboxFade: ReturnType<typeof createSkyboxFade> | null = null;
  const prepareSkyboxFade = (): void => {
    const authoredSkybox = scene.settings?.environment?.skybox;
    if (opts.skipAnimations || !authoredSkybox || !scene.settings?.environment) return;
    const fade = createSkyboxFade(
      engine,
      authoredSkybox,
      config.environment.background.skybox?.fadeInDurationMs ?? SKYBOX_FADE_DURATION_FALLBACK_MS,
    );
    scene.settings.environment = {
      ...scene.settings.environment,
      skybox: { ...authoredSkybox, intensity: 0 },
    };
    skyboxFade = fade;
    cleanups.push(fade.dispose);
  };

  // Orbit-pivot return after a POI fly is SDK-native now — see
  // `cameraController.orbit.pivotReturn` in nativeScenes (SDK C6).

  // SDK-native POI fly. `setCameraState({isAnimated})` IS the engine's smooth
  // fly: in orbit mode it builds an ARC waypoint (spherical around the target,
  // eased) and self-sustains the `active` render tier while animating
  // (datum-engine `isFrameDirty` → true while `isAnimating()`), so we neither
  // interpolate the camera ourselves nor pump frames.
  //
  // We pass a TRAVEL-SCALED duration (NOT the backend POI duration, and NOT the
  // SDK's native focus path which would fall back to a flat 500 ms) so short
  // hops stay snappy. The height clamp is suspended for the flight — its
  // per-frame, distance-interpolated (one-frame-stale) ceiling would otherwise
  // clamp the moving camera each tick → jerk + wrong end. Re-armed shortly after
  // the duration elapses (POI poses are in bounds → no snap). Mirrors Pilgrim,
  // whose POI transition runs with the controls' clamp off.
  const flyTo = (
    position: readonly number[],
    lookAt: readonly number[] | undefined,
    fov: number | undefined,
  ): void => {
    if (disposed) return;
    const cur = engine.getCameraState().position;
    const travel = Math.hypot(position[0] - cur[0], position[1] - cur[1], position[2] - cur[2]);
    const durationMs = flightDurationS(travel, isFps) * 1000;

    const state: { position: number[]; lookAt?: number[]; fov?: number } = {
      position: [position[0], position[1], position[2]],
    };
    if (lookAt != null) state.lookAt = [lookAt[0], lookAt[1], lookAt[2]];
    if (fov != null) state.fov = fov;

    poiFlying = true;
    engine.setCameraConstraints(null);
    // FPS: legacy pre-computed a collision-SAFE end pose (`getSafePosition`,
    // slide: false); the SDK substitute is solving collisions DURING the
    // flight, so a walk-up near the building/hill OBBs can't end inside them.
    engine.setCameraState(state, {
      isAnimated: true,
      animationDuration: durationMs,
      easing: POI_FLIGHT_EASING,
      ...(isFps ? { flightAvoidCollisions: true } : {}),
    });

    if (poiFlyTimer) clearTimeout(poiFlyTimer);
    poiFlyTimer = window.setTimeout(() => {
      poiFlyTimer = 0;
      if (disposed) return;
      poiFlying = false;
      armCameraConstraints(); // restore min-polar after the flight (POI poses are in range)
      engine.requestRender(); // wake the render loop after the fly
    }, durationMs + 80);
  };

  // POI fly-to. Chat POI chips / sight suggestions fire `goToSight`; the scene
  // flies the camera to `poseForSight` (orbit: `orbitPoseForSight`, fps:
  // `fpsPoseForSight`). The chips and the backend POST stay with the chat — only
  // the camera move lives here. 3D marker clicks → backend `poi_click` are
  // bridged below via the `poi:click` event.
  const sightById = new Map(opts.sights.map((s) => [s.id, s]));

  // FPS walk-up pose, computed AT CLICK TIME (legacy SightManager contract):
  // approach the sight from wherever the viewer currently stands — 3D-normalize
  // (camera − sight), step back `cameraConfig.distance` (fallback 2 m) along it,
  // then pin Y back to the CURRENT eye level so the head never drifts
  // vertically. Degenerate camera≈sight falls back to backing away along the
  // current view direction. End-pose collision safety is handled by
  // `flightAvoidCollisions` in flyTo (legacy used a pre-solved getSafePosition).
  const fpsPoseForSight = (
    sight: Sight,
  ): {
    position: [number, number, number];
    lookAt: [number, number, number];
    fov?: number;
  } | null => {
    if (disposed) return null;
    const cam = engine.getCameraState();
    const sp = sight.position;
    let dx = cam.position[0] - sp.x;
    let dy = cam.position[1] - sp.y;
    let dz = cam.position[2] - sp.z;
    if (dx * dx + dy * dy + dz * dz < 1e-6) {
      // Camera is standing on the sight — back away opposite the view direction.
      dx = cam.position[0] - cam.lookAt[0];
      dy = cam.position[1] - cam.lookAt[1];
      dz = cam.position[2] - cam.lookAt[2];
      if (dx * dx + dy * dy + dz * dz < 1e-6) return null;
    }
    const inv = 1 / Math.hypot(dx, dy, dz);
    const d = sight.cameraConfig?.distance ?? 2.0;
    return {
      position: [sp.x + dx * inv * d, cam.position[1], sp.z + dz * inv * d],
      lookAt: [sp.x, sp.y, sp.z],
      fov: sight.cameraConfig?.fov ?? undefined,
    };
  };
  const poseForSight = (sight: Sight) =>
    isFps ? fpsPoseForSight(sight) : orbitPoseForSight(sight);
  const poiIds = new Set(sdkPois.map((p) => p.id));
  // SDK POI ids are `String(sight.id)`; map back to the original SightId so a
  // marker click can fire the backend `poi_click` (see the poi:click bind).
  const sightIdByPoiId = new Map(opts.sights.map((s) => [String(s.id), s.id]));
  // "Inside the Kaaba"-style POIs are an EXCEPTION: their sight carries
  // `moveToSceneId` → an inside scene. A click must PUSH that scene onto the
  // stack (like a chat scene-banner), NOT run the normal poi_click / camera-fly
  // flow. (Such sights are filtered out of the chip tour by the backend, so
  // this only ever concerns the 3D marker.)
  const insideSceneTargetForPoi = (poiId: string): { sight: Sight; target: string } | null => {
    const sid = sightIdByPoiId.get(poiId);
    const sight = sid !== undefined ? sightById.get(sid) : undefined;
    const target = sight?.moveToSceneId;
    if (!sight || typeof target !== 'string' || !isInsideScene(target)) return null;
    return { sight, target };
  };
  const unwatchGoToSight = goToSight.watch(({ id, smooth }) => {
    if (disposed) return;
    const idStr = String(id);
    // Registered POI → `engine.selectPoi`: the SDK marks the marker
    // `.dsdk-is-selected` (active/yellow styling) AND flies natively to the
    // POI's `camera` block. This keeps the chat chip in sync with the 3D marker
    // selection (a silent manual fly left the marker un-highlighted).
    if (poiIds.has(idStr)) {
      // Re-selecting the SAME poi is a no-op in the SDK (it early-returns
      // before `poi:select`), so nothing would fly — but legacy re-flew on
      // every pick of a sight, chip or marker alike, and that matters now that
      // `home` keeps the selection through its aim return (nativeScenes'
      // `HOME_AIM_RETURN.disablePoiDeselect`): after the return the framing is
      // stale while the POI is still selected, and the obvious way back is to
      // click it again. Clear first so the select is a real transition.
      if (String(engine.getSelectedPoi()?.id ?? '') === idStr) engine.clearPoiSelection();
      engine.selectPoi(idStr);
      return;
    }
    // Non-POI sight (no marker): manual fly. Smooth → SDK animated transition;
    // `smooth: false` → instant teleport.
    const sight = sightById.get(id);
    const pose = sight ? poseForSight(sight) : null;
    if (!sight || !pose) return;
    if (smooth ?? true) {
      flyTo(pose.position, pose.lookAt, pose.fov);
    } else {
      engine.setCameraState(
        pose.fov != null
          ? { position: pose.position, lookAt: pose.lookAt, fov: pose.fov }
          : { position: pose.position, lookAt: pose.lookAt },
      );
    }
  });
  cleanups.push(unwatchGoToSight);

  // Fly the camera on POI selection — covers BOTH a 3D marker click and a chat
  // chip (which routes through `engine.selectPoi`). Native focus-on-select is
  // off (annotations.disableFocusOnSelect), so this is the single fly path.
  // FPS pois carry NO static camera block — the walk-up pose is computed here,
  // at selection time, from the current camera.
  bind('poi:select', ({ poi }) => {
    if (disposed) return;
    // Nav beacon → no camera fly, no selection semantics (click selection is
    // off when only beacons exist; this guard covers a future mixed scene).
    if (String(poi.id).startsWith('nav:')) return;
    // Inside-scene nav POI → no camera fly; the poi:click bridge enters the scene.
    if (insideSceneTargetForPoi(String(poi.id))) return;
    if (isFps) {
      const sid = sightIdByPoiId.get(String(poi.id));
      const sight = sid !== undefined ? sightById.get(sid) : undefined;
      const pose = sight ? fpsPoseForSight(sight) : null;
      if (pose) flyTo(pose.position, pose.lookAt, pose.fov);
      return;
    }
    const cam = poi.camera;
    if (!cam?.position) return;
    flyTo(cam.position, cam.lookAt, cam.fov ?? undefined);
  });

  // A 3D MARKER click → mirror AssistantHud.handlePoiSelect EXACTLY (= a chip
  // click) so the backend tour comes back and the chip's visited state flips.
  // CRITICAL: the `poi_click` POST (→ tour → visited) is driven by
  // `userSelectedSight({ source: 'poi' })` (→ poiClickRequestGenerated → POST),
  // NOT by `assistantPoiClickRequested` (that only feeds the chat prompt) —
  // firing the latter alone left visited un-updated. `userSelectedSight` also
  // drives the camera (→ goToSight → selectPoi → poi:select → flyTo); the SDK's
  // own click selection then early-returns (already selected) → no
  // double fly. `assistantPoiClickRequested` adds the chat prompt, matching the
  // chip. The SDK emits `poi:click` ONLY on a real marker pointer-up, NOT on the
  // programmatic selectPoi a chip triggers — so chips never reach here (no dup).
  bind('poi:click', ({ poi }) => {
    if (disposed) return;
    const idStr = String(poi.id);
    // Nav beacon (`overall` root) → the navigation cinematic. MUST run before
    // the sight lookup: beacons are synthetic, not in sightIdByPoiId.
    // Suppresses userSelectedSight / assistantPoiClickRequested (legacy rule —
    // no backend sight exists, no chat side-effect wanted).
    if (idStr.startsWith('nav:')) {
      navigateToScene(idStr.slice(4));
      return;
    }
    const sightId = sightIdByPoiId.get(idStr);
    if (sightId === undefined) return;
    // Exception: an inside-scene nav POI ("Inside the Kaaba" → kaaba-inside)
    // pushes the inside scene onto the stack instead of the poi_click flow.
    const inside = insideSceneTargetForPoi(String(poi.id));
    if (inside) {
      insidePoiClicked({
        sightId: String(poi.id),
        sightName: inside.sight.name ?? null,
        targetSceneId: inside.target,
      });
      enterInsideScene(inside.target);
      return;
    }
    userSelectedSight({ id: sightId, smooth: true, source: 'poi' });
    assistantPoiClickRequested({ id: sightId });
  });

  // --- loading FSM bridge: engine events → Pilgrim sceneLoading store ------
  // Each state transition fires ONCE (the SDK can re-emit the same state).
  let lastSplatBytes = -1;
  let finished = false;
  let previewFired = false;
  let fetchFired = false;
  let initFired = false;
  // Dual gate (legacy `splatRevealDone && flyoverDone`): the scene counts as
  // loaded only when BOTH the splat reveal has completed ('done' state) AND
  // the preloader cinematic has landed. Without a cinematic the second flag
  // starts true and this degenerates to the old single-gate behaviour.
  let revealReachedScene = false;
  let modelFailed = false;
  // Legacy `crowProp` (src/scene/sdk/crow/): the bird perches from the reveal on
  // and opens the scene with its tour. The tour starts once both gates pass, and
  // the scene counts as loaded (chat, HUD, markers, input) only after it lands.
  // A restore and the dev skip go without it, like the rest of the preloader.
  let crow: SdkCrow | null = null;
  const mountCrow = (): void => {
    if (crow || !config.crowProp) return;
    crow = mountSdkCrow(engine, config.crowProp, config.environment, config.id, isDevMode);
    cleanups.push(crow.dispose);
  };
  const crowTourPlanned = Boolean(config.crowProp?.tour) && !opts.skipAnimations;
  let crowTour: 'pending' | 'running' | 'done' = crowTourPlanned ? 'pending' : 'done';
  const onCrowTourDone = (): void => {
    if (disposed) return;
    crowTour = 'done';
    maybeFinishLoading();
  };
  const maybeFinishLoading = (): void => {
    if (finished || !revealReachedScene || !cinematicDone) return;
    if (crowTour === 'pending' && crow) {
      crowTour = 'running';
      // Legacy: no markers at all while the camera follows the bird.
      hidePoiLayer();
      crow.playOpeningTour(onCrowTourDone);
      return;
    }
    if (crowTour === 'running') return;
    finished = true;
    // Input unblocks + markers fade in only when BOTH gates pass — a skipped
    // flyover whose glide lands mid-reveal keeps the blocker up until the
    // reveal completes, exactly like legacy (its blocker hides at
    // sceneLoadingFinished, and poiMarkersEnabledTime keys off it + 200 ms).
    releaseCinematicInput();
    if (cinematic || crowTourPlanned) {
      window.setTimeout(() => {
        if (!disposed) showPoiLayer();
      }, HUD_REVEAL_DELAY);
    }
    sceneLoadingFinished();
  };
  // The user grabbing an orbit scene (legacy `onOrbitInteractionStart`): live
  // once the scene has loaded — legacy's controls were off under the preloader.
  if (!isFps) cleanups.push(watchSceneGrab(container, () => finished && !disposed));
  if (new URLSearchParams(window.location.search).has('tier')) {
    cleanups.push(mountTierOverlay(engine, qualityProfile));
  }
  const onCinematicDone = (): void => {
    if (disposed || cinematicDone) return;
    cinematicDone = true;
    // Restore the collisions the cinematic lifted, then the deferred constraint
    // arm (site 2 of the pitchConstraint note): the last route has landed
    // (phase 'done') on the routes' rest pose, which is inside the limits and
    // the cage — no snap.
    restoreCinematicCollisions();
    armCameraConstraints();
    maybeFinishLoading();
  };
  // Manual-reveal scheduling (paired with nativeScenes `transitionMode: 'manual'`).
  // The reveal fires ~800 ms after the splat loads so the progress bar is gone first:
  // bar hits 100% at splatPhase 'ready', preloaderDone hides it +400 ms later (App.tsx),
  // then it fades over 400 ms (SceneProgressBar.module.css). Tune if that timing changes.
  const REVEAL_AFTER_BAR_MS = 800;
  let revealScheduled = false;
  let revealTimer: ReturnType<typeof setTimeout> | 0 = 0;

  // Progress coalescing. The SDK emits `scene:progress` in rapid SYNCHRONOUS
  // bursts (per decoded chunk, off a worker `onmessage`). Dispatching the
  // React-coupled Effector event on EACH fires 50+ synchronous store updates in
  // one tick → React throws "Maximum update depth exceeded" at the enqueue stage
  // (the stack is fully synchronous, no component render frame). The legacy
  // fetch path doesn't hit this — its stream reads are async, so React flushes
  // between chunks. Fix: keep only the latest sample and flush it ONCE per
  // animation frame → at most one store update per frame.
  let pendingProgress: { loadedBytes: number; totalBytes: number | null } | null = null;
  let progressRaf = 0;
  const flushProgress = (): void => {
    progressRaf = 0;
    if (disposed || finished || !pendingProgress) return;
    splatLoadingProgressed(pendingProgress);
    pendingProgress = null;
  };

  // idle → loading
  sceneLoadingStarted();

  bind('scene:progress', (event) => {
    if (finished) return;
    // 0/0 samples arrive while only the preview is up — no splat bytes yet, and
    // the first sample flips the bar to 'fetching'.
    const { loadedBytes, totalBytes } = event;
    if (loadedBytes === 0 || loadedBytes === lastSplatBytes) return;
    lastSplatBytes = loadedBytes;
    pendingProgress = { loadedBytes, totalBytes: totalBytes || null };
    if (!progressRaf) progressRaf = requestAnimationFrame(flushProgress);
  });
  bind('scene:state-changed', (event) => {
    switch (event.curState) {
      // The 'preview' phase-entry state is intentionally NOT handled — it fires at
      // splat ~0% (before the preview model paints). The Pilgrim `preview` status is
      // gated on `scene:preview-shown` (bound below) so the preloader hands off only
      // when the model is actually on screen (no black gap). SDK CR R5.
      case 'reveal':
        // Fallback: a scene without a preview model never emits `scene:preview-shown`
        // — advance the preview status here so the preloader still hands off. A
        // scene that authors one and gets here without it never showed it: the
        // model failed (the engine's `warn` says why) or came after the reveal.
        if (!previewFired) {
          previewFired = true;
          if (scene.settings?.appearance?.previewModelUrl) scenePreviewMissed();
          scenePreviewLoadingFinished();
        }
        // The bird comes up with the scene.
        mountCrow();
        // (splatInitCompleted / bar → 100% already fired on `scene:loaded`. In manual
        // `transitionMode` this state arrives only AFTER we call setSceneState('done').)
        break;
      case 'done':
        // A failed model counts as settled, so a load whose splat failed reaches
        // 'done' too — right before its terminal `scene:error`. Nothing revealed.
        if (modelFailed) break;
        if (!initFired) {
          initFired = true;
          splatInitCompleted();
        }
        revealReachedScene = true;
        // The sky comes up with the revealed scene — fade it in from here.
        skyboxFade?.start();
        // Whichever of 'reveal' / 'done' comes first mounts the bird.
        mountCrow();
        maybeFinishLoading();
        break;
    }
  });
  // `scene:preview-shown` = preview model is actually on screen (glTF loaded + added
  // to the scene), NOT the 'preview' phase-entry (fires at splat ~0%, before paint).
  // Gate the Pilgrim `preview` status on THIS → the particle-sphere preloader hands
  // off exactly when the preview model appears (no black gap). SDK CR R5.
  bind('scene:preview-shown', () => {
    if (!previewFired) {
      previewFired = true;
      scenePreviewLoadingFinished();
    }
    // Preview model is on screen → begin the tour (enter → turntable). The
    // turntable spins until the splat is ready (requestExit below) — legacy
    // PreviewTour semantics. No-op re-entry guard lives in the cinematic.
    cinematic?.start();
  });
  bind('scene:loaded', () => {
    // `scene:loaded` = splat fully downloaded + parsed. In manual `transitionMode` the
    // SDK does NOT auto-reveal, so this is the real "loading done" moment: drive the
    // Pilgrim bar all the way to 100% here (fetch-complete → 'initializing', then
    // init-complete → 'ready' = 100%). preloaderDone (+400 ms) then fades the bar out.
    if (!fetchFired) {
      fetchFired = true;
      splatFetchCompleted();
    }
    if (!initFired) {
      // Dev hold: `splatInitCompleted` flips splatPhase → 'ready', which is
      // what hides SceneTitle + SceneProgressBar (preloaderDone +400 ms).
      // Delay it by the hold so the preloader UI lives through the extended
      // turntable — the bar then hits 100% exactly when the hold ends, and
      // the existing +800 ms bar-fade window lines up with the tour exit,
      // same as a genuinely slow download. Not on the skipAnimations path.
      if (preloaderHoldMs > 0 && !opts.skipAnimations) {
        initFired = true;
        const initHoldTimer = setTimeout(() => {
          if (!disposed) splatInitCompleted();
        }, preloaderHoldMs);
        cleanups.push(() => clearTimeout(initHoldTimer));
      } else {
        initFired = true;
        splatInitCompleted();
      }
    }
    // Trigger the reveal only AFTER the bar has reached 100% and faded away, so it
    // never plays under a visible loading bar (the reported "reveal at ~90%"). The
    // restore / skipAnimations path reveals instantly in loadScene().then() below, so
    // don't also schedule the timer there.
    if (!opts.skipAnimations && !revealScheduled) {
      revealScheduled = true;
      if (cinematic) {
        // Dev hold: keep the turntable spinning for preloaderHoldMs AFTER the
        // splat is ready — the whole exit → reveal → flyover sequence (incl.
        // requestExit, which would stop the spin) starts only after the hold.
        const holdTimer = setTimeout(() => {
          if (disposed) return;
          // Legacy interlock: wind the tour down (1 s eased exit to
          // previewTourEnd) in PARALLEL with the bar fade-out, then start the
          // reveal and the flyover on the same tick. POI markers are hidden
          // first — the SDK would show them at 'done' (mid-flight) otherwise.
          const barGone = new Promise<void>((resolve) => {
            revealTimer = setTimeout(() => {
              revealTimer = 0;
              resolve();
            }, REVEAL_AFTER_BAR_MS);
          });
          void Promise.all([cinematic.requestExit(), barGone]).then(() => {
            if (disposed) return;
            hidePoiLayer();
            engine.setSceneState('done');
            void cinematic.startFlyover().then(onCinematicDone);
          });
        }, preloaderHoldMs);
        cleanups.push(() => clearTimeout(holdTimer));
      } else {
        revealTimer = setTimeout(() => {
          revealTimer = 0;
          if (!disposed) engine.setSceneState('done');
        }, REVEAL_AFTER_BAR_MS + preloaderHoldMs);
      }
    }
  });
  bind('model:error', () => {
    modelFailed = true;
  });
  // A non-terminal error is a rejected settings block the load recovers from.
  // A terminal one ends the load — and the cinematic with it, or its pump would
  // keep the loop at full rate under the error screen.
  bind('scene:error', (event) => {
    if (!event.terminal) return;
    cinematic?.dispose();
    sceneLoadingFailed({ stage: 'splat', error: event.error });
  });

  // The SDK fetches the assets itself, past our CDN fallback: a CDN asset that
  // does not answer is swapped for its local copy first.
  const sceneLoad = swapUnreachableCdnAssets(scene, config.id).then(() => {
    if (disposed) return;
    prepareSkyboxFade();
    const load = engine.loadScene(scene);
    // `loadScene` applies the scene's collisions in its synchronous part, so a
    // cinematic lifts them right here — before its first route, and whether or
    // not a preview ever shows (the no-preview path parks and flies without a
    // `scene:preview-shown`).
    if (cinematic) suspendCinematicCollisions();
    return load;
  });
  sceneLoad
    .then(() => {
      if (disposed) return;
      // Snapshot restore (inside-scene return): land the camera exactly where
      // it was left and skip the reveal cinematic.
      if (opts.initialCameraPose) {
        const p = opts.initialCameraPose;
        engine.setCameraState({
          position: [p.position.x, p.position.y, p.position.z],
          lookAt: [p.target.x, p.target.y, p.target.z],
          fov: p.fov,
        });
      } else if (opts.skipAnimations && SDK_PRELOADER_CINEMATIC_SCENES.has(config.id)) {
        // Cinematic scenes author `settings.camera` as the flight's STARTING
        // frame; with the flights skipped, land where the routes would have —
        // legacy skipAnimations finalize.
        const rest = preloaderRestPose(scene.settings?.cameraRoutes ?? []);
        if (rest) engine.setCameraState(rest);
      }
      if (opts.skipAnimations) {
        engine.setSceneState('done', { skipAnimation: true });
      }
      // AFTER the restore pose — `loadScene` starts unconstrained and an
      // earlier arm would be wiped. Cinematic scenes defer to onCinematicDone
      // instead: setCameraState clamps by active constraints, and the flight
      // legitimately leaves the limits (see the pitchConstraint note above).
      if (!cinematic) armCameraConstraints();
      // Snapshot-save pose producer (`sceneState.ts`). Enables the inside-scene
      // enter/return contract: chat + camera pose restore on goBack.
      sdkCameraPoseGetterReady(getCurrentCameraPose);
      cleanups.push(() => sdkCameraPoseGetterReady(null));
      // Chat context "what is on screen" note — legacy's getPoiMarkerDiagnostics.
      sdkSceneMarkersGetterReady(() => readSceneMarkers(container, engine, sightIdByPoiId));
      cleanups.push(() => sdkSceneMarkersGetterReady(null));

      // Dev toggle `unlockFpsMovement` ("requires reload"): un-zero the five
      // authored movement speeds on look-only FPS scenes. Scene-layer settings
      // beat the authored zeros; `undefined` REMOVES an explicit zero so the
      // SDK's base.speed derivations kick back in. One-shot only —
      // setSceneCameraControllerSettings is side-effect-heavy (resets gravity/
      // pivot/damping) and must never ride a per-change slider.
      if (isFps && isDevMode && devConfig.unlockFpsMovement) {
        const current = engine.getSceneCameraControllerSettings() ?? {};
        engine.setSceneCameraControllerSettings({
          ...current,
          fps: {
            ...(current.fps ?? {}),
            moveSpeed: 2,
            scrollSpeed: undefined,
            pinchSpeed: undefined,
            slideSpeed: undefined,
            tapHoldMoveSpeed: undefined,
          },
        });
      }

      // Dev panel handles (SplatDevPanel, FlightEditorPanel) — dev mode or
      // `?flight` only; production without them keeps the store null and the
      // panels inert.
      if (isDevMode || hasFlightParam) {
        // `finished`, not `cinematicDone`: a skip glide can land before the
        // reveal, and the input barrier holds the controls off until both pass.
        // A POI flight re-arms the constraints itself when it lands.
        const editorTools = createDevEditorTools(engine, {
          canTakeCamera: () => finished && !navTransitionPending && !poiFlying,
          armCameraConstraints: () => {
            if (!poiFlying) armCameraConstraints();
          },
          collisions: !!scene.settings?.collisions?.enabled,
        });
        cleanups.push(editorTools.dispose);
        splatDevHandlesReady({ engine, config, sights: opts.sights, renderSettings, editorTools });
        cleanups.push(() => splatDevHandlesReady(null));
        // Live watch for the constraints-unlock and orbit-marker toggles —
        // registered AFTER the scene loads (the watch fires immediately on
        // subscribe; pre-load it would poke an engine without a scene). A
        // persisted `true` from a previous session applies right here on the
        // first fire. Not under `?flight` alone: no panel there turns an
        // unlock off, and Play would stop clamping as the preloader does.
        if (isDevMode) {
          cleanups.push(
            $devConfig.watch((dev) => {
              if (disposed) return;
              applyConstraintsUnlock(dev.unlockCameraConstraints);
              // A second GL context: only where the panel can turn it off again,
              // never from a flag persisted by an earlier `?dev` session.
              editorTools.setOrbitCenterShown(hasDevParam && dev.showOrbitCenterMarker);
            }),
          );
        }
      }
    })
    .catch((error) => {
      if (disposed) return;
      cinematic?.dispose();
      sceneLoadingFailed({ stage: 'splat', error });
    });

  const getCurrentCameraPose = (): SplatCameraPose => {
    if (disposed) return EMPTY_POSE;
    const s = engine.getCameraState();
    return {
      position: { x: s.position[0], y: s.position[1], z: s.position[2] },
      target: { x: s.lookAt[0], y: s.lookAt[1], z: s.lookAt[2] },
      fov: s.fov,
    };
  };

  // Overlay slide fly-to and the navigation cinematic: a host-built
  // one-waypoint route — a straight line with the skip's cubic-bezier (an
  // animated `setCameraState` would arc round the pivot instead). A new flight
  // stops the one in the air and restarts from the live pose (legacy
  // transitionToPose semantics); only a flight that lands runs its callback.
  // A route applies no orbit limits in the air, so a tab jump across the map
  // tips the view down at the ground beneath it for ~0.1 s — accepted on sight.
  const FLIGHT_ROUTE_ID = 'overall-flight';
  let onFlightLanded: (() => void) | null = null;
  bind('route:end', (event) => {
    if (event.routeId !== FLIGHT_ROUTE_ID) return;
    const landed = onFlightLanded;
    onFlightLanded = null;
    // After the engine's hand-back (controls-mode restore, pivot carry).
    queueMicrotask(() => {
      if (!disposed) landed?.();
    });
  });
  const flyRoute = (pose: OverviewCameraPose, durationS: number, onLanded?: () => void): void => {
    onFlightLanded = null;
    if (engine.getActiveCameraRouteId() === FLIGHT_ROUTE_ID) engine.stopCameraRoute();
    engine.putCameraRoute({
      id: FLIGHT_ROUTE_ID,
      waypoints: [
        {
          target: [pose.position.x, pose.position.y, pose.position.z],
          lookAt: [pose.target.x, pose.target.y, pose.target.z],
          duration: Math.max(durationS, 1e-3) * 1000,
          easing: EASE_IN_OUT_CUBIC,
        },
      ],
    });
    onFlightLanded = onLanded ?? null;
    engine.startCameraRoute(FLIGHT_ROUTE_ID);
  };

  const transitionToPose = (pose: OverviewCameraPose, durationSec = 1.5): void => {
    if (disposed) return;
    // Input is off for the flight, as in legacy transitionToPose: the route
    // leaves a live gesture rotate-only, not off. A flight that a newer one
    // stops never re-enables it; the newest does, unless the preloader or a
    // navigation still holds input.
    engine.setRuntimeOptions({ controls: { enabled: false } });
    flyRoute(pose, durationSec, () => {
      if (!disposed && finished && !navTransitionPending) {
        engine.setRuntimeOptions({ controls: { enabled: true } });
      }
    });
  };

  // --- navigation cinematic (`overall` root → child scene) -----------------
  // Port of legacy `runNavigationCinematic`: eased fly toward the nav-POI
  // (position = poi + current-view-dir × orbitMinDistance), scene swap fired
  // at 70% of the flight (ease-in-out has covered ~89% of the path by then;
  // the tail overlaps React unmount + the next scene's preloader fade-in).
  // `engine.destroy()` mid-flight is safe (verified: rAF cancelled, route/tick
  // state dies with the engine).
  let navTransitionPending = false;
  const navigateToScene = (targetSceneId: string): void => {
    if (disposed) return;
    if (!KNOWN_SCENES.has(targetSceneId)) {
      console.warn('[createSdkScene] navigation target unknown:', targetSceneId);
      return;
    }
    if (navTransitionPending) return;
    navTransitionPending = true;

    const triggerSceneChange = (): void => {
      resetNavigation();
      enterScene({
        sceneId: targetSceneId,
        poiFilterId: SCENE_POI_ID[targetSceneId] ?? targetSceneId,
      });
      sceneEntered(targetSceneId);
    };

    const targetPoi = (config.navigationPois ?? []).find((p) => p.targetSceneId === targetSceneId);
    if (!targetPoi) {
      // No cinematic anchor for this target — instant switch (legacy parity).
      triggerSceneChange();
      return;
    }

    const cam = engine.getCameraState();
    const dirX = cam.position[0] - cam.lookAt[0];
    const dirY = cam.position[1] - cam.lookAt[1];
    const dirZ = cam.position[2] - cam.lookAt[2];
    const dirLen = Math.hypot(dirX, dirY, dirZ) || 1;
    const distance = config.controls?.orbitMinDistance ?? 2;
    const newPosition = {
      x: targetPoi.position.x + (dirX / dirLen) * distance,
      y: targetPoi.position.y + (dirY / dirLen) * distance,
      z: targetPoi.position.z + (dirZ / dirLen) * distance,
    };

    // Duration profile: number | {close, far, thresholdDistance} | default.
    const profile = config.navigationCinematicDuration;
    const travel = Math.hypot(
      newPosition.x - cam.position[0],
      newPosition.y - cam.position[1],
      newPosition.z - cam.position[2],
    );
    const durationS =
      typeof profile === 'number'
        ? profile
        : profile
          ? travel > profile.thresholdDistance
            ? profile.far
            : profile.close
          : 2.5;

    // Input barrier for the flight — the scene dies at the 70% swap anyway;
    // re-entry is blocked by navTransitionPending (+ the overlay's own ref).
    engine.setRuntimeOptions({ controls: { enabled: false } });

    let triggered = false;
    const triggerOnce = (): void => {
      if (triggered) return;
      triggered = true;
      triggerSceneChange();
    };
    const earlyTimer = window.setTimeout(triggerOnce, Math.round(durationS * 1000 * 0.7));
    cleanups.push(() => clearTimeout(earlyTimer));
    flyRoute(
      { position: newPosition, target: targetPoi.position },
      durationS,
      triggerOnce, // idempotent safety net if the timer is throttled
    );
  };

  const restPose = SDK_PRELOADER_CINEMATIC_SCENES.has(config.id)
    ? preloaderRestPose(scene.settings?.cameraRoutes ?? [])
    : null;

  // PoiArtifact opens over the scene (App): legacy stops its loop, input and
  // markers and drops its drawing buffer to 1×1 while the artifact's own context
  // lives. The SDK has no pause, but a hidden host takes no pointer
  // input, controls off silence the keyboard, an idle engine parks, and the
  // engine sizes its canvas to its host — 1×1 host, 1×1 buffer.
  let paused = false;
  const pause = (): void => {
    if (disposed || paused) return;
    paused = true;
    engine.setRuntimeOptions({ controls: { enabled: false } });
    container.style.visibility = 'hidden';
    container.style.width = '1px';
    container.style.height = '1px';
  };
  const resume = (): void => {
    if (disposed || !paused) return;
    paused = false;
    container.style.removeProperty('width');
    container.style.removeProperty('height');
    container.style.removeProperty('visibility');
    if (finished && !navTransitionPending) {
      engine.setRuntimeOptions({ controls: { enabled: true } });
    }
    engine.requestRender();
  };

  const api: SplatSceneApi = {
    dispose: () => {
      if (disposed) return;
      disposed = true;
      if (progressRaf) cancelAnimationFrame(progressRaf);
      if (poiFlyTimer) clearTimeout(poiFlyTimer);
      if (revealTimer) clearTimeout(revealTimer);
      cleanups.forEach((fn) => fn());
      stopContextLossTracking();
      engine.destroy();
      decrementWebGLContext('splat');
      decrementScene('splat');
      trackPostDispose('sdkEngine', engine, { sceneId: config.id });
      // Holders of `api` (React state, App callbacks) must not pin this
      // closure — and through it the engine.
      api.dispose = NOOP;
      api.transitionToPose = NOOP;
      api.navigateToScene = NOOP;
      api.pause = NOOP;
      api.resume = NOOP;
      api.getCurrentCameraPose = EMPTY_POSE_GETTER;
    },
    pause,
    resume,
    getCurrentCameraPose,
    transitionToPose,
    restPose: restPose
      ? {
          position: { x: restPose.position[0], y: restPose.position[1], z: restPose.position[2] },
          target: { x: restPose.lookAt[0], y: restPose.lookAt[1], z: restPose.lookAt[2] },
        }
      : undefined,
    navigateToScene,
  };
  return api;
};
